import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  TablePagination,
  Fade,
  CircularProgress,
  useTheme,
  Switch,
  FormControlLabel,
  useMediaQuery,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  MilitaryTech as LevelIcon,
  MonetizationOn as MoneyIcon,
  Search as SearchIcon,
  Stars as ActiveIcon,
  EmojiEvents as MaxIcon,
} from '@mui/icons-material';

// components
import { Typography } from '../../../components/Wrappers/Wrappers';
import AlertModal from '../../../components/AlertModal';
import ConfirmModal from '../../../components/ConfirmModal';
import RefreshButton from '../../../components/RefreshButton/RefreshButton';
import levelService from '../../../services/level';

const GlassCard = ({ children, sx, ...props }) => {
  const theme = useTheme();
  return (
    <Card
      {...props}
      sx={{
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
        ...sx,
      }}
    >
      {children}
    </Card>
  );
};

const Incentive = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [designations, setDesignations] = useState([]);
  const [allIncentives, setAllIncentives] = useState([]);

  const [formData, setFormData] = useState({
    levelCode: '',
    rankName: '',
    designationId: '',
    minTeamBusiness: '',
    percentage: '',
    minSelfBusiness: '',
    incentiveAmount: '',
    benefitType: 'incentive',
    isRoyaltyApplicable: false,
    status: 'active',
  });

  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', action: null });

  useEffect(() => {
    fetchIncentives();
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => {
    fetchDesignations();
    fetchAllIncentives();
  }, []);

  const handleRefresh = () => {
    fetchIncentives();
    fetchAllIncentives();
    fetchDesignations();
  };

  const fetchDesignations = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${baseURL}/api/globalMasterDetails?type=designation`);
      if (response.data.success) {
        setDesignations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
    }
  };

  const fetchAllIncentives = async () => {
    try {
      const params = {
        benefitType: 'incentive',
        limit: 1000, // Fetch all to filter out existing ranks
      };
      const response = await levelService.getLevels(params);
      if (response.status && Array.isArray(response.data)) {
        setAllIncentives(response.data);
      }
    } catch (error) {
      console.error('Error fetching all incentives:', error);
    }
  };

  const fetchIncentives = async () => {
    try {
      setLoading(true);
      const params = {
        benefitType: 'incentive',
        page,
        limit: rowsPerPage,
        search,
        status: statusFilter === 'all' ? '' : statusFilter
      };
      const response = await levelService.getLevels(params);
      if (response.status && Array.isArray(response.data)) {
        setIncentives(response.data);
        setTotalCount(response.extra?.total || response.data.length);
      } else {
        setIncentives([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching incentives:', error);
      setAlertData({ open: true, message: 'Failed to fetch incentive data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        levelCode: item.designationLevel ? `L${item.designationLevel}` : item.levelCode || '',
        rankName: item.designationName || item.rankName || '',
        designationId: item.designationId || '',
        minTeamBusiness: item.toTeamBusiness || item.minTeamBusiness || '',
        percentage: item.percentage || '',
        minSelfBusiness: item.toSelfBusiness || item.minSelfBusiness || '',
        incentiveAmount: item.incentiveAmount || '',
        benefitType: item.benefitType || 'incentive',
        isRoyaltyApplicable: item.isRoyaltyApplicable === true,
        status: item.status === 'active' || item.status === true ? 'active' : 'inactive',
      });
    } else {
      setEditingItem(null);
      setFormData({
        levelCode: '',
        rankName: '',
        designationId: '',
        minTeamBusiness: '',
        percentage: '',
        minSelfBusiness: '',
        incentiveAmount: '',
        benefitType: 'incentive',
        isRoyaltyApplicable: false,
        status: 'active'
      });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!formData.rankName || !formData.incentiveAmount) {
      setAlertData({ open: true, message: 'Please fill Rank and Incentive amount', type: 'error' });
      return;
    }

    try {
      const payload = {
        ...formData,
        rewardAmount: 0,
        rewardItem: "",
        rewardDescription: "",
        benefitType: 'incentive',
        status: formData.status === 'active' ? 'active' : 'inactive',
        minTeamBusiness: Number(formData.minTeamBusiness),
        minSelfBusiness: Number(formData.minSelfBusiness),
        percentage: Number(formData.percentage),
        incentiveAmount: Number(formData.incentiveAmount),
      };

      let response;
      if (editingItem) {
        response = await levelService.updateLevel(editingItem._id, payload);
      } else {
        response = await levelService.createLevel(payload);
      }

      if (response.status) {
        setAlertData({ open: true, message: 'Incentive structure updated', type: 'success' });
        setOpen(false);
        fetchIncentives();
        fetchAllIncentives();
      }
    } catch (error) {
      console.error('Error saving incentive:', error);
      setAlertData({ open: true, message: error.message || 'Failed to save incentive', type: 'error' });
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Rank',
      message: 'Are you sure you want to delete this rank?',
      action: async () => {
        try {
          const response = await levelService.deleteLevel(id);
          if (response.status) {
            setAlertData({ open: true, message: 'Rank deleted successfully', type: 'success' });
            fetchIncentives();
            fetchAllIncentives();
          }
        } catch (error) {
          setAlertData({ open: true, message: 'Failed to delete rank', type: 'error' });
        } finally {
          setConfirmDialog({ ...confirmDialog, open: false });
        }
      }
    });
  };


  return (
    <Box sx={{
      pt: { xs: 1, md: 2 },
      px: { xs: 2, md: 4 },
      pb: { xs: 2, md: 4 },
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }}>
      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2
      }}>
        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.5 }}>
            Incentive Structure
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Manage rank-based incentives and performance targets.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          <RefreshButton onClick={handleRefresh} loading={loading} />
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              px: { xs: 2, sm: 3 },
              py: 1,
              fontWeight: 700,
              width: { xs: '100%', sm: 'auto' },
              bgcolor: theme.palette.primary.main,
              boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}40`,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                boxShadow: `0 6px 16px 0 ${theme.palette.primary.main}50`
              }
            }}
          >
            Add New Rank
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Max Incentive', value: allIncentives.length > 0 ? `₹${Math.max(...allIncentives.map(i => i.incentiveAmount || 0)).toLocaleString()}` : '₹0', icon: <MaxIcon />, color: theme.palette.primary.main },
          { label: 'Top Percentage', value: allIncentives.length > 0 ? `${Math.max(...allIncentives.map(i => i.percentage || 0))}%` : '0%', icon: <TrendingUpIcon />, color: theme.palette.success.main },
          { label: 'Total Ranks', value: allIncentives.length, icon: <LevelIcon />, color: theme.palette.error.main },
          { label: 'Active Ranks', value: allIncentives.filter(i => i.status === 'active' || i.status === true).length, icon: <ActiveIcon />, color: theme.palette.warning.main },
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <GlassCard>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                <Avatar sx={{
                  background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}99 100%)`,
                  color: '#fff',
                  width: 48, height: 48,
                  borderRadius: '12px',
                  boxShadow: `0 4px 12px 0 ${stat.color}30`
                }}>
                  {stat.icon}
                </Avatar>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>{stat.label.toUpperCase()}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>{stat.value}</Typography>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <GlassCard sx={{ mb: 4, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by rank name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.5)' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.5)' } }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </TextField>
          </Grid>
        </Grid>
      </GlassCard>

      {/* Table */}
      <Paper sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead sx={{ bgcolor: 'rgba(244, 247, 254, 0.5)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>LEVEL</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>RANK</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>TEAM BUSINESS</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>PERCENTAGE</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>SELF BUSINESS</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>INCENTIVE</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incentives.map((item, index) => (
                  <TableRow key={item._id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' }, transition: '0.2s' }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.9rem' }}>
                        {item.designationLevel ? `L${item.designationLevel}` : item.levelCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.9rem' }}>
                        {item.designationName || item.rankName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                      ₹{(item.toTeamBusiness || item.minTeamBusiness || 0).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip label={`${item.percentage}%`} size="small" sx={{ fontWeight: 700, bgcolor: `${theme.palette.success.main}15`, color: theme.palette.success.main }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                      ₹{(item.toSelfBusiness || item.minSelfBusiness || 0).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MaxIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mr: 0.5 }} />
                        <Typography sx={{ fontWeight: 800, color: theme.palette.primary.main }}>₹{item.incentiveAmount?.toLocaleString()}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpen(item)} size="small" sx={{ color: theme.palette.info.main, mr: 1 }}><EditIcon fontSize="small" /></IconButton>
                      <IconButton onClick={() => handleDelete(item._id)} size="small" sx={{ color: theme.palette.error.main }}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 15]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{
            borderTop: '1px solid rgba(0,0,0,0.05)',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontWeight: 700,
              color: 'text.secondary',
              fontSize: '0.75rem'
            }
          }}
        />
      </Paper>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '16px', p: 1 } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, color: 'text.primary', pt: 3 }}>
          {editingItem ? 'Edit Incentive Rank' : 'Create Incentive Rank'}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={designations
                  .filter(d => {
                    const levelCode = `L${d.details?.level || ''}`;
                    return editingItem?.levelCode === levelCode ||
                      editingItem?.designationId === d._id ||
                      !allIncentives.some(inc =>
                        (inc.levelCode === levelCode && levelCode !== 'L') ||
                        inc.designationId === d._id ||
                        (inc.designationName || inc.rankName) === d.details?.designationName
                      );
                  })
                  .map((d) => d.details?.designationName || '')}
                value={formData.rankName}
                onChange={(event, newValue) => {
                  const selectedRank = newValue;
                  const designation = designations.find(d => d.details.designationName === selectedRank);
                  if (designation) {
                    setFormData({
                      ...formData,
                      levelCode: `L${designation.details.level || ''}`,
                      rankName: selectedRank,
                      designationId: designation._id,
                      minTeamBusiness: designation.details.toAmount || '',
                      minSelfBusiness: designation.details.toSelfBusiness || designation.details.minSelfBusiness || '',
                      percentage: designation.details.commission || '',
                    });
                  } else {
                    setFormData({ ...formData, rankName: selectedRank || '', designationId: '' });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Rank (Designation)"
                    placeholder="Select or type rank..."
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Level Code" variant="outlined" value={formData.levelCode} onChange={(e) => setFormData({ ...formData, levelCode: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Incentive Amount" type="number" variant="outlined" value={formData.incentiveAmount} onChange={(e) => setFormData({ ...formData, incentiveAmount: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Team Business Target" type="number" variant="outlined" value={formData.minTeamBusiness} onChange={(e) => setFormData({ ...formData, minTeamBusiness: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Percentage (%)" type="number" variant="outlined" value={formData.percentage} onChange={(e) => setFormData({ ...formData, percentage: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Self Business Target" type="number" variant="outlined" value={formData.minSelfBusiness} onChange={(e) => setFormData({ ...formData, minSelfBusiness: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} /></Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ px: 1 }}>
                <FormControlLabel
                  control={<Switch checked={formData.isRoyaltyApplicable} onChange={(e) => setFormData({ ...formData, isRoyaltyApplicable: e.target.checked })} color="primary" />}
                  label={<Typography sx={{ fontWeight: 600, color: 'text.primary' }}>Royalty Applicable</Typography>}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ px: 1 }}>
                <FormControlLabel
                  control={<Switch checked={formData.status === 'active'} onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })} color="primary" />}
                  label={<Typography sx={{ fontWeight: 600, color: 'text.primary' }}>Active Status</Typography>}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ fontWeight: 700, color: 'text.secondary' }}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              bgcolor: theme.palette.primary.main,
              borderRadius: '8px',
              px: 4,
              fontWeight: 700,
              boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}40`,
              '&:hover': { bgcolor: theme.palette.primary.dark }
            }}
          >
            Save Rank
          </Button>
        </DialogActions>
      </Dialog>

      <AlertModal open={alertData.open} message={alertData.message} type={alertData.type} onClose={() => setAlertData({ ...alertData, open: false })} />
      <ConfirmModal open={confirmDialog.open} title={confirmDialog.title} message={confirmDialog.message} onConfirm={() => confirmDialog.action()} onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })} />
    </Box>
  );
};

export default Incentive;
